// SweepReveal — entrance transition built around a T.accent layer with a
// curved edge. Two variants:
//
//   rise (default)  the layer climbs in from below the screen, covers the
//                   viewport, and exits the top revealing the children.
//                   Used when the screen underneath was something else
//                   (resume relock: the app is visible, the sweep covers it,
//                   then reveals the lock UI).
//
//   lift            the layer STARTS covering the viewport, holds a beat,
//                   then lifts off the top. Used when a crimson cover is
//                   already on screen (cold launch: storyboard → webview →
//                   boot cover are all T.accent), so the handoff is seamless
//                   instead of the layer visibly re-entering from the bottom.
//
// Both run ~480ms. onComplete fires from the sweep layer's animationend
// event — the real end of the animation, not a guessed timeout — so callers
// can sequence work (the lock screen waits for it before requesting Face
// ID). prefers-reduced-motion swaps in a plain 200ms fade whose animationend
// drives the same callback.
//
// Children render immediately but start transparent; they turn opaque while
// the layer covers the viewport, so nothing is ever half revealed. The layer
// is oversized (140vw x 140vh, offset left) so only the gentle curve of its
// edge is visible on screen.

import { useMemo, useRef } from 'react';
import { T } from '../theme';

const reducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

const SweepReveal = ({ children, style, variant = 'rise', onComplete }) => {
  const reduce = useMemo(reducedMotion, []);
  const doneRef = useRef(false);
  const finish = (e, expected) => {
    if (e.animationName !== expected || doneRef.current) return;
    doneRef.current = true;
    onComplete?.();
  };

  if (reduce) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
        <div
          style={{ width: '100%', height: '100%', animation: 'sweepFadeIn 0.2s ease both' }}
          onAnimationEnd={(e) => finish(e, 'sweepFadeIn')}
        >
          {children}
        </div>
        <style>{`@keyframes sweepFadeIn{from{opacity:0}to{opacity:1}}`}</style>
      </div>
    );
  }

  const anim = variant === 'lift' ? 'sweepLift' : 'sweepRise';
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', ...style }}>
      <div style={{ width: '100%', height: '100%', opacity: 0, animation: `sweepContentIn 0.12s ease ${variant === 'lift' ? '0.1s' : '0.2s'} both` }}>
        {children}
      </div>
      <div
        aria-hidden="true"
        onAnimationEnd={(e) => finish(e, anim)}
        style={{
          position: 'absolute',
          left: '-20vw',
          top: 0,
          width: '140vw',
          height: '140vh',
          background: T.accent,
          borderRadius: '50% 50% 50% 50% / 6vh 6vh 6vh 6vh',
          transform: variant === 'lift' ? 'translateY(-10%)' : 'translateY(110%)',
          animation: `${anim} 0.48s cubic-bezier(0.55,0,0.25,1) both`,
          pointerEvents: 'none',
        }}
      />
      <style>{`
        @keyframes sweepRise{
          0%{transform:translateY(110%)}
          45%{transform:translateY(-10%)}
          100%{transform:translateY(-115%)}
        }
        @keyframes sweepLift{
          0%{transform:translateY(-10%)}
          25%{transform:translateY(-10%)}
          100%{transform:translateY(-115%)}
        }
        @keyframes sweepContentIn{from{opacity:0}to{opacity:1}}
      `}</style>
    </div>
  );
};

export default SweepReveal;
